import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { createPortal } from "react-dom";
import MaterialReactTable from "material-react-table";
import { ExportToCsv } from "export-to-csv";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import lambdaFetch from "../functions/FetchFromLambda";
import { AntTabs, AntTab } from "../components/common/Tabs";
import { dateTimeSecondFormat, createDateTimeSecondFormatter } from "../utils/formatter";
import StatementsTable from "./StatementsTable";
import UsersTable from "./UsersTable";
import PermissionBuilder from "./PermissionBuilder";
import SystemSettings from "./SystemSettings";
const { REACT_APP_STAGE } = process.env

const csvOptions = {
  fieldSeparator: ",",
  quoteStrings: '"',
  decimalSeparator: ".",
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const csvExporter = new ExportToCsv(csvOptions);

// Table configuration with display names and descriptions
const TABLES_CONFIG = {
  users: {
    name: "Users",
    endpoint: "users",
    tableName: `apix_${REACT_APP_STAGE}_sys_users`,
    primaryKey: "ID",
    description: "System users and their access configurations",
    editable: true,
    autoGenerateId: true,
    idPrefix: "USER_",
    customColumns: [
      { accessorKey: "username", header: "Username", size: 200 },
      { accessorKey: "long_name", header: "Long Name", size: 200 },
      { accessorKey: "roles", header: "App Roles", size: 200 },
      { accessorKey: "data_role", header: "Data Role", size: 200 },
      { accessorKey: "active", header: "Active", size: 100 },
    ],
  },
  roles: {
    name: "Roles",
    endpoint: "roles",
    tableName: `apix_${REACT_APP_STAGE}_sys_roles`,
    primaryKey: "ID",
    description: "System data/app role configurations",
    editable: true,
    autoGenerateId: true,
    idPrefix: "ROLE_",
    customColumns: [
      { accessorKey: "role_name", header: "Role Name", size: 200 },
      { accessorKey: "type", header: "Role Type", size: 200 },
      { accessorKey: "description", header: "Description", size: 300 },
      { accessorKey: "active", header: "Active", size: 100 },
    ],
  },
  statements: {
    name: "Data Role Statements",
    endpoint: "statements",
    tableName: `apix_${REACT_APP_STAGE}_sys_data_role_statements`,
    primaryKey: "id",
    description: "Data access control statements",
    editable: true,
    autoGenerateId: true,
    idPrefix: "STMT_",
    customColumns: [
      //   { accessorKey: "id", header: "ID", size: 180 },
      { accessorKey: "data_role", header: "Data Role", size: 150 },
      { accessorKey: "query", header: "Query", size: 200 },
    //   { accessorKey: "statement", header: "Statement", size: 400 },
      { accessorKey: "filter_logic", header: "Filter Logic", size: 400 },
      { accessorKey: "active", header: "Active", size: 100 },
    ],
  },
  "audit-log": {
    name: "Audit Log",
    endpoint: "audit-log",
    tableName: `apix_${REACT_APP_STAGE}_sys_audit_log`,
    primaryKey: "id",
    sortKey: "timestamp",
    description: "System audit log - read only",
    editable: false,
    compositeKey: true,
    autoGenerateId: true,
    idPrefix: "AUDIT_",
    readOnly: true,
    customColumns: [
      {
        accessorKey: "timestamp",
        header: "Timestamp",
        size: 180,
        enableSorting: true,
        sortingFn: "datetime",
      },
      { accessorKey: "user", header: "User", size: 150 },
      { accessorKey: "action", header: "Action", size: 120 },
      { accessorKey: "page", header: "Page", size: 120 },
      { accessorKey: "resource", header: "Resource", size: 200 },
      { accessorKey: "payload", header: "Payload", size: 200 },
    ],
  },
  "role-definition": {
    name: "App Role Definitions",
    endpoint: "role-definition",
    tableName: `apix_${REACT_APP_STAGE}_sys_role_definition`,
    primaryKey: "ID",
    description: "Role definitions and their permissions",
    editable: true,
    autoGenerateId: true,
    idPrefix: "ROLE_DEF_",
    hideIdInTable: true, // Hide ID column in table view
    customColumns: [
      { accessorKey: "role", header: "Role", size: 200 },
      { accessorKey: "permissions", header: "Permissions", size: 400 },
      { accessorKey: "active", header: "Active", size: 100 },
    ],
  },
  "system-settings": {
    name: "System Settings",
    endpoint: "system-settings",
    description: "System-wide configuration settings",
    isCustomComponent: true, // Flag to render custom component instead of table
  },
};

// Tiles that require super_admin role - not accessible to other users
const SUPER_ADMIN_ONLY_TABS = ["roles", "statements", "role-definition"];

function hasSuperAdmin(credentials) {
  return credentials?.user?.super_admin === true;
}

export default function DynamoDbTables(props) {
  const { credentials } = props.fetchInitialData;
  const isSuperAdmin = hasSuperAdmin(credentials);
  const systemTimezone = credentials?.systemTimezone || 'America/New_York';
  const dateTimeSecondTzFormat = createDateTimeSecondFormatter(systemTimezone);

  // Check if we should restore a specific tab after reload (e.g., after saving timezone)
  const getInitialTab = () => {
    const savedTab = sessionStorage.getItem("adminTabAfterReload");
    if (savedTab) {
      sessionStorage.removeItem("adminTabAfterReload"); // Clear it after reading
      // Don't restore restricted tabs if user doesn't have super_admin
      if (SUPER_ADMIN_ONLY_TABS.includes(savedTab) && !isSuperAdmin) {
        return "users";
      }
      return savedTab;
    }
    return "users"; // Default tab
  };

  const [currentTab, setCurrentTab] = useState(getInitialTab());

  // Redirect away from restricted tabs if user loses access (e.g. session change)
  useEffect(() => {
    if (
      !isSuperAdmin &&
      SUPER_ADMIN_ONLY_TABS.includes(currentTab)
    ) {
      setCurrentTab("users");
    }
  }, [isSuperAdmin, currentTab]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' or 'edit'
  const [currentItem, setCurrentItem] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [availableAppRoles, setAvailableAppRoles] = useState([]);
  const [payloadModalOpen, setPayloadModalOpen] = useState(false);
  const [payloadData, setPayloadData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [auditFilters, setAuditFilters] = useState({
    user: "",
    startDate: "",
    endDate: "",
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemPendingDelete, setItemPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const normalizeDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const startOfDay = new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
    );
    return startOfDay.toISOString();
  };

  const normalizeDateEndOfDay = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const endOfDay = new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
      23,
      59,
      59,
      999,
    );
    return endOfDay.toISOString();
  };

  const sortKeysDeep = (value) => {
    if (Array.isArray(value)) {
      return value.map(sortKeysDeep);
    }
    if (value && typeof value === "object") {
      const sortedEntries = Object.entries(value).sort(([a], [b]) =>
        a.localeCompare(b),
      );
      return sortedEntries.reduce((acc, [key, val]) => {
        acc[key] = sortKeysDeep(val);
        return acc;
      }, {});
    }
    return value;
  };

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 1000,
    totalCount: 0,
    lastEvaluatedKey: null,
    hasNextPage: false,
  });

  const currentConfig = TABLES_CONFIG[currentTab];

  // Fetch data for a specific table with pagination
  const fetchTableData = async (
    tableKey,
    pageIndex = 0,
    pageSize = 1000,
    lastEvaluatedKey = null,
    filtersOverride = null,
  ) => {
    setLoading((prev) => ({ ...prev, [tableKey]: true }));
    setError((prev) => ({ ...prev, [tableKey]: null }));

    try {
      const endpoint = `dynamo/${TABLES_CONFIG[tableKey].endpoint}/scan`;
      const effectivePageSize = tableKey === "audit-log" ? 1000 : pageSize;

      const requestBody = {
        limit: effectivePageSize,
        lastEvaluatedKey,
      };

      const filters =
        filtersOverride ?? (tableKey === "audit-log" ? auditFilters : null);

      if (tableKey === "audit-log" && filters) {
        const filterExpressionParts = [];
        const expressionAttributeNames = {};
        const expressionAttributeValues = {};

        // Note: User filter is handled client-side for case-insensitive matching
        // Only date filters are applied server-side for efficiency

        const startIso = normalizeDate(filters.startDate);
        const endIso = normalizeDateEndOfDay(filters.endDate);

        if (startIso && endIso) {
          filterExpressionParts.push(
            "#timestamp BETWEEN :startTime AND :endTime",
          );
          expressionAttributeNames["#timestamp"] = "timestamp";
          expressionAttributeValues[":startTime"] = startIso;
          expressionAttributeValues[":endTime"] = endIso;
        } else if (startIso) {
          filterExpressionParts.push("#timestamp >= :startTime");
          expressionAttributeNames["#timestamp"] = "timestamp";
          expressionAttributeValues[":startTime"] = startIso;
        } else if (endIso) {
          filterExpressionParts.push("#timestamp <= :endTime");
          expressionAttributeNames["#timestamp"] = "timestamp";
          expressionAttributeValues[":endTime"] = endIso;
        }

        if (filterExpressionParts.length > 0) {
          requestBody.filterExpression = filterExpressionParts.join(" AND ");
          if (Object.keys(expressionAttributeNames).length > 0) {
            requestBody.expressionAttributeNames = expressionAttributeNames;
          }
          if (Object.keys(expressionAttributeValues).length > 0) {
            requestBody.expressionAttributeValues = expressionAttributeValues;
          }
        }
      }

      const response = await lambdaFetch(
        endpoint,
        "post",
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        "",
        credentials,
      );

      let parsedData = [];
      let paginationInfo = {
        lastEvaluatedKey: null,
        count: 0,
        scannedCount: 0,
      };

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          parsedData = response.data;
        } else if (response.data.body) {
          let bodyObj = response.data.body;
          if (typeof response.data.body === "string") {
            try {
              bodyObj = JSON.parse(response.data.body);
            } catch (e) {
              console.error("❌ Failed to parse body:", e);
              setError((prev) => ({
                ...prev,
                [tableKey]: "Failed to parse response body",
              }));
              setLoading((prev) => ({ ...prev, [tableKey]: false }));
              return;
            }
          }

          let finalData = bodyObj?.data;
          if (typeof bodyObj?.data === "string") {
            try {
              finalData = JSON.parse(bodyObj.data);
            } catch (e) {
              console.error("❌ Failed to parse body.data:", e);
            }
          }

          parsedData = Array.isArray(finalData)
            ? finalData
            : finalData
              ? [finalData]
              : [];

          paginationInfo = {
            lastEvaluatedKey: bodyObj?.lastEvaluatedKey || null,
            count: bodyObj?.count || parsedData.length,
            scannedCount: bodyObj?.scannedCount || parsedData.length,
          };
        } else if (response.data.data) {
          parsedData = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
          paginationInfo = {
            lastEvaluatedKey: response.data.lastEvaluatedKey || null,
            count: response.data.count || parsedData.length,
            scannedCount: response.data.scannedCount || parsedData.length,
          };
        } else {
          console.error("❌ Unknown response.data structure:", response.data);
          setError((prev) => ({
            ...prev,
            [tableKey]: "Unknown response format",
          }));
        }

        let normalizedData = parsedData;
        if (tableKey === "role-definition") {
          normalizedData = parsedData.map((item) => {
            if (item && typeof item.permissions === "string") {
              try {
                const parsed = JSON.parse(item.permissions);
                return { ...item, permissions: parsed };
              } catch (e) {
                return item;
              }
            }
            return item;
          });
        }

        // Apply case-insensitive user filter for audit-log (client-side)
        if (tableKey === "audit-log") {
          const filters = filtersOverride ?? auditFilters;
          const trimmedUser = (filters?.user || "").trim();
          if (trimmedUser) {
            const userFilterLower = trimmedUser.toLowerCase();
            normalizedData = normalizedData.filter((item) => {
              const itemUser = item?.user || "";
              return itemUser.toLowerCase() === userFilterLower;
            });
          }
        }

        setData((prev) => ({
          ...prev,
          [tableKey]: normalizedData,
        }));

        setPagination((prev) => ({
          ...prev,
          pageIndex: 0, // Always stay on page 0 since we're showing all records
          pageSize: effectivePageSize,
          lastEvaluatedKey: paginationInfo.lastEvaluatedKey,
          hasNextPage: !!paginationInfo.lastEvaluatedKey,
          totalCount: normalizedData.length, // Use filtered data length
        }));

        setError((prev) => ({ ...prev, [tableKey]: null }));
      } else if (response === false) {
        console.error("❌ DynamoDB Tables - API returned false");
        setError((prev) => ({
          ...prev,
          [tableKey]:
            "Failed to fetch data. Check browser console for details.",
        }));
      } else if (!response) {
        console.error("❌ DynamoDB Tables - No response received");
        setError((prev) => ({
          ...prev,
          [tableKey]:
            "No response from API. Check if the Lambda is deployed and the endpoint exists.",
        }));
      } else {
        console.error("❌ DynamoDB Tables - Error response:", response);
        setError((prev) => ({
          ...prev,
          [tableKey]:
            response?.error ||
            `Unexpected response: ${JSON.stringify(response)}`,
        }));
      }
    } catch (err) {
      console.error(`Error fetching ${tableKey}:`, err);
      setError((prev) => ({
        ...prev,
        [tableKey]: err.message || "Failed to fetch data",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [tableKey]: false }));
    }
  };

  // Load data when tab changes
  useEffect(() => {
    // Reset pagination when switching tabs - always use 1000 records
    setPagination({
      pageIndex: 0,
      pageSize: 1000,
      totalCount: 0,
      lastEvaluatedKey: null,
      hasNextPage: false,
    });

    if (!data[currentTab]) {
      fetchTableData(currentTab, 0, 1000, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // Load all tables on mount
  useEffect(() => {
    fetchTableData("users", 0, 1000, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleRefresh = () => {
    setPagination({
      pageIndex: 0,
      pageSize: 1000,
      totalCount: 0,
      lastEvaluatedKey: null,
      hasNextPage: false,
    });

    fetchTableData(
      currentTab,
      0,
      1000,
      null,
      currentTab === "audit-log" ? auditFilters : null,
    );
  };

  const handleAuditFilterChange = (field, value) => {
    setAuditFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyAuditFilters = () => {
    const filters = {
      user: auditFilters.user.trim(),
      startDate: auditFilters.startDate,
      endDate: auditFilters.endDate,
    };

    setPagination({
      pageIndex: 0,
      pageSize: 1000,
      totalCount: 0,
      lastEvaluatedKey: null,
      hasNextPage: false,
    });

    fetchTableData("audit-log", 0, 1000, null, filters);
  };

  const handleClearAuditFilters = () => {
    const clearedFilters = { user: "", startDate: "", endDate: "" };
    setAuditFilters(clearedFilters);

    setPagination({
      pageIndex: 0,
      pageSize: 1000,
      totalCount: 0,
      lastEvaluatedKey: null,
      hasNextPage: false,
    });

    fetchTableData("audit-log", 0, 1000, null, clearedFilters);
  };

  const handlePaginationChange = (newPageIndex, newPageSize) => {
    const effectiveNewPageSize =
      currentTab === "audit-log" ? 1000 : newPageSize;
    const filtersForTable = currentTab === "audit-log" ? auditFilters : null;

    if (effectiveNewPageSize !== pagination.pageSize) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        pageSize: effectiveNewPageSize,
        totalCount: 0,
        lastEvaluatedKey: null,
        hasNextPage: false,
      }));

      fetchTableData(
        currentTab,
        0,
        effectiveNewPageSize,
        null,
        filtersForTable,
      );
    } else if (newPageIndex !== pagination.pageIndex) {
      let lastEvaluatedKeyToUse = null;

      if (newPageIndex > pagination.pageIndex) {
        lastEvaluatedKeyToUse = pagination.lastEvaluatedKey;
      } else if (newPageIndex < pagination.pageIndex) {
        lastEvaluatedKeyToUse = null;
      }

      setPagination((prev) => ({
        ...prev,
        pageIndex: newPageIndex,
      }));

      fetchTableData(
        currentTab,
        newPageIndex,
        effectiveNewPageSize,
        lastEvaluatedKeyToUse,
        filtersForTable,
      );
    }
  };

  const fetchAvailableAppRoles = async () => {
    try {
      // Fetch roles from the roles table with filter for active=true and type="app"
      const endpoint = "dynamo/roles/scan";
      const requestBody = {
        limit: 1000,
        filterExpression: "#active = :active AND #type = :type",
        expressionAttributeNames: {
          "#active": "active",
          "#type": "type",
        },
        expressionAttributeValues: {
          ":active": true,
          ":type": "app",
        },
      };

      const response = await lambdaFetch(
        endpoint,
        "post",
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        "",
        credentials,
      );

      let roles = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          roles = response.data;
        } else if (response.data.body) {
          const bodyObj =
            typeof response.data.body === "string"
              ? JSON.parse(response.data.body)
              : response.data.body;
          const finalData = typeof bodyObj?.data === "string"
            ? JSON.parse(bodyObj.data)
            : bodyObj?.data;
          roles = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
        } else if (response.data.data) {
          roles = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        }

        // Extract role_name from each role and filter out any null/undefined values
        const appRoles = roles
          .map((role) => role.role_name)
          .filter((name) => name != null && name !== "");

        setAvailableAppRoles(appRoles);
      } else {
        setAvailableAppRoles([]);
      }
    } catch (error) {
      console.error("❌ Error fetching available app roles:", error);
      setAvailableAppRoles([]);
    }
  };

  const handleAddClick = async () => {
    setDialogMode("create");
    setEditingItem(null);

    // Set default values for new items
    const defaultItem = {};

    // Set default status/active based on table
    if (currentTab === "client-values") {
      defaultItem.active = true;
    } else if (currentTab === "role-val-pairs") {
      defaultItem.active = true;
      // Note: role-val-pairs table is currently commented out in TABLES_CONFIG
    } else if (currentTab === "role-definition") {
      defaultItem.active = true;
      await fetchAvailableAppRoles(); // Fetch app roles for role definition
    } else if (currentTab === "roles") {
      defaultItem.active = true;
    }

    setCurrentItem(defaultItem);

    setDialogOpen(true);
  };

  const handleEditClick = async (item) => {
    setDialogMode("edit");
    setCurrentItem({ ...item });
    setEditingItem(item);

    if (currentTab === "role-definition") {
      await fetchAvailableAppRoles();
    }

    setDialogOpen(true);
  };

  const handleDeleteClick = (item) => {
    setItemPendingDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = (event, reason) => {
    // Only close on button click, not on backdrop click or escape key
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    setDeleteConfirmOpen(false);
    setItemPendingDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemPendingDelete) {
      return;
    }

    setDeleting(true);

    try {
      // Check if deleting a role-definition that is assigned to users
      if (currentTab === "role-definition") {
        const roleToDelete = itemPendingDelete.role;
        
        if (roleToDelete) {
          // Ensure users data is loaded
          let usersData = data.users || [];
          if (usersData.length === 0) {
            // Fetch users data if not already loaded
            setLoading((prev) => ({ ...prev, users: true }));
            try {
              const endpoint = `dynamo/${TABLES_CONFIG.users.endpoint}/scan`;
              const requestBody = { limit: 1000 };
              const response = await lambdaFetch(
                endpoint,
                "post",
                credentials.user.accessToken,
                JSON.stringify(requestBody),
                "",
                credentials,
              );
              
              if (response && response.data) {
                let parsedData = [];
                if (Array.isArray(response.data)) {
                  parsedData = response.data;
                } else if (response.data.body) {
                  let bodyObj = response.data.body;
                  if (typeof response.data.body === "string") {
                    bodyObj = JSON.parse(response.data.body);
                  }
                  const finalData = typeof bodyObj?.data === "string"
                    ? JSON.parse(bodyObj.data)
                    : bodyObj?.data;
                  parsedData = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
                } else if (response.data.data) {
                  parsedData = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
                }
                usersData = parsedData;
                // Update state for future use
                setData((prev) => ({ ...prev, users: parsedData }));
              }
            } catch (err) {
              console.error("Error fetching users for validation:", err);
            } finally {
              setLoading((prev) => ({ ...prev, users: false }));
            }
          }
          
          // Check if any user has this role assigned
          const usersWithRole = usersData.filter((user) => {
            if (!user.roles) return false;
            
            // Handle both array and comma-separated string formats
            const userRoles = Array.isArray(user.roles)
              ? user.roles
              : typeof user.roles === "string"
              ? user.roles.split(",").map((r) => r.trim())
              : [];
            
            return userRoles.includes(roleToDelete);
          });
          
          if (usersWithRole.length > 0) {
            // Show error message
            setSnackbarMessage(
              `Cannot delete role "${roleToDelete}". It is assigned to ${usersWithRole.length} user(s): ${usersWithRole.map((u) => u.username || u.ID).join(", ")}`
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setDeleteConfirmOpen(false);
            setItemPendingDelete(null);
            setDeleting(false);
            return;
          }
        }
      }

      const primaryKeyValue = itemPendingDelete[currentConfig.primaryKey];
      const sortKeyValue = currentConfig.compositeKey
        ? itemPendingDelete[currentConfig.sortKey]
        : null;

      let path = `dynamo/${currentConfig.endpoint}/item/${primaryKeyValue}`;
      if (sortKeyValue) {
        path += `/${sortKeyValue}`;
      }

      const response = await lambdaFetch(
        path,
        "delete",
        credentials.user.accessToken,
        null,
        "Item deleted successfully",
        credentials,
      );

      if (response && response.success) {
        fetchTableData(currentTab);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbarMessage("Error deleting item. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setItemPendingDelete(null);
    }
  };

  const handleDialogClose = (event, reason) => {
    // Only close on button click, not on backdrop click or escape key
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    setDialogOpen(false);
    setCurrentItem({});
    setEditingItem(null);
  };

  const handlePayloadClick = (payload) => {
    setPayloadData(payload);
    setPayloadModalOpen(true);
  };

  const handlePayloadModalClose = (event, reason) => {
    // Only close on button click, not on backdrop click or escape key
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    setPayloadModalOpen(false);
    setPayloadData(null);
  };

  const handleSave = async () => {
    try {
      let response;

      if (dialogMode === "create") {
        // Auto-generate primary key if needed
        let itemToCreate = { ...currentItem };

        if (
          currentConfig.autoGenerateId &&
          !itemToCreate[currentConfig.primaryKey]
        ) {
          const timestamp = Date.now();
          const random = Math.random().toString(36).substr(2, 9);
          itemToCreate[currentConfig.primaryKey] =
            `${currentConfig.idPrefix}${timestamp}_${random}`;
        }

        // For role-definition, ensure permissions is stored as an object (Map in DynamoDB)
        if (
          currentTab === "role-definition" &&
          itemToCreate.permissions &&
          typeof itemToCreate.permissions === "string"
        ) {
          try {
            itemToCreate.permissions = JSON.parse(itemToCreate.permissions);
          } catch (e) {
            // leave as-is if not valid JSON
          }
        }

        // For role-definition table, check for duplicate role
        if (currentTab === "role-definition") {
          if (!itemToCreate.role) {
            setSnackbarMessage("Role is a required field");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }

          const existingRoleDef = data[currentTab]?.find(
            (item) => item.role === itemToCreate.role
          );

          if (existingRoleDef) {
            setSnackbarMessage(
              `A role definition with Role "${itemToCreate.role}" already exists. Each role can only have one definition.`
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }
        }

        // For roles table, check for duplicate role_name + type combination
        if (currentTab === "roles") {
          if (!itemToCreate.role_name || !itemToCreate.type) {
            setSnackbarMessage("Role Name and Type are required fields");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }

          const existingRole = data[currentTab]?.find(
            (item) =>
              item.role_name === itemToCreate.role_name &&
              item.type === itemToCreate.type
          );

          if (existingRole) {
            setSnackbarMessage(
              `A role with Role Name "${itemToCreate.role_name}" and Type "${itemToCreate.type}" already exists. Please use a different combination.`
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }
        }

        // CREATE
        response = await lambdaFetch(
          `dynamo/${currentConfig.endpoint}`,
          "post",
          credentials.user.accessToken,
          JSON.stringify(itemToCreate),
          "Item created successfully",
          credentials,
        );
      } else {
        // UPDATE
        const primaryKeyValue = editingItem[currentConfig.primaryKey];
        const sortKeyValue = currentConfig.compositeKey
          ? editingItem[currentConfig.sortKey]
          : null;

        let path = `dynamo/${currentConfig.endpoint}/item/${primaryKeyValue}`;
        if (sortKeyValue) {
          path += `/${sortKeyValue}`;
        }

        // Remove primary key, sort key, and auto-generated fields from updates
        const updates = { ...currentItem };
        delete updates[currentConfig.primaryKey];
        if (currentConfig.sortKey) {
          delete updates[currentConfig.sortKey];
        }
        delete updates.created; // Never update created timestamp
        delete updates.updated; // Remove if present
        delete updates.id; // Remove id if present (for statements)

        // For role-definition, ensure permissions is stored as an object (Map in DynamoDB)
        if (
          currentTab === "role-definition" &&
          updates.permissions &&
          typeof updates.permissions === "string"
        ) {
          try {
            updates.permissions = JSON.parse(updates.permissions);
          } catch (e) {
            // leave as-is if not valid JSON
          }
        }

        // For role-definition table, check for duplicate role
        if (currentTab === "role-definition") {
          // Get the role from updates (or use existing value if not being updated)
          const role = updates.role !== undefined ? updates.role : editingItem.role;

          if (!role) {
            setSnackbarMessage("Role is a required field");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }

          // Check if another role definition (excluding the current one being edited) has the same role
          const duplicateRoleDef = data[currentTab]?.find(
            (item) => {
              // Skip the current item being edited
              if (item[currentConfig.primaryKey] === editingItem[currentConfig.primaryKey]) {
                return false;
              }
              return item.role === role;
            }
          );

          if (duplicateRoleDef) {
            setSnackbarMessage(
              `A role definition with Role "${role}" already exists. Each role can only have one definition.`
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }
        }

        // For roles table, check for duplicate role_name + type combination
        if (currentTab === "roles") {
          // Get the role_name and type from updates (or use existing values if not being updated)
          const roleName = updates.role_name !== undefined ? updates.role_name : editingItem.role_name;
          const roleType = updates.type !== undefined ? updates.type : editingItem.type;

          if (!roleName || !roleType) {
            setSnackbarMessage("Role Name and Type are required fields");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }

          // Check if another role (excluding the current one being edited) has the same combination
          const duplicateRole = data[currentTab]?.find(
            (item) => {
              // Skip the current item being edited
              if (item[currentConfig.primaryKey] === editingItem[currentConfig.primaryKey]) {
                return false;
              }
              return item.role_name === roleName && item.type === roleType;
            }
          );

          if (duplicateRole) {
            setSnackbarMessage(
              `A role with Role Name "${roleName}" and Type "${roleType}" already exists. Please use a different combination.`
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
          }
        }

        response = await lambdaFetch(
          path,
          "put",
          credentials.user.accessToken,
          JSON.stringify(updates),
          "Item updated successfully",
          credentials,
        );
      }

      if (response && response.success) {
        handleDialogClose();
        fetchTableData(currentTab);
      } else {
        console.error("❌ Save failed:", response);
      }
    } catch (error) {
      console.error("❌ Error saving item:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setCurrentItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Generate columns dynamically based on data for MaterialReactTable
  const columns = useMemo(() => {
    // Check if table has custom column configuration
    if (currentConfig && currentConfig.customColumns) {
      // Separate status and active columns from other columns
      const statusActiveCols = [];
      const otherCols = [];

      currentConfig.customColumns.slice().forEach((col) => {
        if (col.accessorKey === "status" || col.accessorKey === "active") {
          statusActiveCols.push(col);
        } else {
          otherCols.push(col);
        }
      });

      // Combine other columns first, then status/active columns
      const orderedCols = [...otherCols, ...statusActiveCols];
      const cols = orderedCols.map((col) => ({
        ...col,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          
          if (typeof value === "object" && value !== null) {
            // Special handling for permissions field - make it prettier
            if (col.accessorKey === "permissions") {
              const jsonStr = JSON.stringify(value, null, 2);
              return (
                <Tooltip
                  title={
                    <pre
                      style={{
                        fontSize: "0.75rem",
                        margin: 0,
                        maxWidth: "600px",
                        maxHeight: "400px",
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                      }}
                    >
                      {jsonStr}
                    </pre>
                  }
                  arrow
                  placement="left"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "white",
                        color: "black",
                        border: "1px solid #e0e0e0",
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        maxWidth: "650px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.7rem",
                      cursor: "help",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "250px",
                      display: "inline-block",
                    }}
                  >
                    {jsonStr.substring(0, 100)}
                    {jsonStr.length > 100 ? "..." : ""}
                  </Typography>
                </Tooltip>
              );
            }
            return (
              <pre style={{ fontSize: "0.75rem", margin: 0 }}>
                {JSON.stringify(value, null, 2)}
              </pre>
            );
          }
          // Highlight active field
          if (col.accessorKey === "active") {
            return (
              <Chip
                label={value ? "Yes" : "No"}
                size="small"
                color={value ? "success" : undefined}
                sx={!value ? { bgcolor: "grey.400", color: "white" } : {}}
              />
            );
          }
          // Highlight status field
          if (col.accessorKey === "status") {
            const isActive =
              value === "active" || value === "Active" || value === true;
            return (
              <Chip
                label={isActive ? "Active" : "Inactive"}
                size="small"
                color={isActive ? "success" : undefined}
                sx={!isActive ? { bgcolor: "grey.400", color: "white" } : {}}
              />
            );
          }
          // Special display for roles field - show as comma-separated string
          if (col.accessorKey === "roles") {
            if (!value) return "-";

            const rolesString = Array.isArray(value) ? value.join(", ") : value;

            return rolesString;
          }
          // Format created timestamp
          if (col.accessorKey === "created") {
            if (!value) return "-";
            return dateTimeSecondTzFormat(value);
          }
          // Highlight primary key (but not in statements table where we want to show it normally)
          if (
            col.accessorKey === currentConfig.primaryKey &&
            currentTab !== "statements"
          ) {
            return <Chip label={value} size="small" color="primary" />;
          }
          // Highlight sort keys (except R_KEY in client-values and timestamp in audit-log)
          if (col.accessorKey === currentConfig.sortKey) {
            if (currentTab === "client-values" && col.accessorKey === "R_KEY") {
              return value; // Plain text for client-values key
            }
            if (currentTab === "audit-log" && col.accessorKey === "timestamp") {
              return dateTimeSecondTzFormat(value);
            }
            return <Chip label={value} size="small" color="primary" />;
          }

          // Special handling for payload column in audit log
          if (col.accessorKey === "payload" && currentTab === "audit-log") {
            if (!value) return "-";

            // Try to parse the payload to see if it's valid JSON
            let parsedPayload = null;
            try {
              parsedPayload = JSON.parse(value);
            } catch (e) {
              // If it's not valid JSON, show as plain text
              return (
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "180px",
                  }}
                  title={value}
                >
                  {value}
                </Typography>
              );
            }

            // If it's valid JSON, show as clickable link
            return (
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "180px",
                }}
                onClick={() => handlePayloadClick(parsedPayload)}
                title="Click to view JSON"
              >
                View Payload
              </Typography>
            );
          }

          return value;
        },
      }));

      // Add actions column (only for editable tables)
      if (!currentConfig.readOnly) {
        cols.push({
          accessorKey: "actions",
          header: "Actions",
          size: 120,
          enableColumnFilter: false,
          enableSorting: false,
          enableHiding: false,
          enableColumnOrdering: false,
          enableResizing: false,
          muiTableHeadCellProps: {
            align: "center",
          },
          muiTableBodyCellProps: {
            align: "center",
          },
          Cell: ({ row }) => (
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(row.original)}
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(row.original)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        });
      }
      return cols;
    }

    // If we have a custom config but no data yet, return empty array to avoid auto-generation
    if (currentConfig && currentConfig.customColumns) {
      return [];
    }

    // Auto-generate columns from data
    const tableData = data[currentTab] || [];
    if (!tableData || tableData.length === 0) {
      return [];
    }

    const firstItem = tableData[0];
    if (!firstItem || typeof firstItem !== "object") {
      return [];
    }

    const fields = Object.keys(firstItem);

    // Separate status and active fields from other fields
    const statusActiveFields = [];
    const otherFields = [];

    fields.forEach((field) => {
      if (field === "status" || field === "active") {
        statusActiveFields.push(field);
      } else {
        otherFields.push(field);
      }
    });

    // Combine other fields first, then status/active fields
    const orderedFields = [...otherFields, ...statusActiveFields];

    const cols = orderedFields.map((field) => ({
      accessorKey: field,
      header: field.replace(/_/g, " ").toUpperCase(),
      size: 150,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (typeof value === "object" && value !== null) {
          return (
            <pre style={{ fontSize: "0.75rem", margin: 0 }}>
              {JSON.stringify(value, null, 2)}
            </pre>
          );
        }
        if (
          field === currentConfig.primaryKey ||
          field === currentConfig.sortKey
        ) {
          // Don't show chips for audit-log timestamp (it's already formatted)
          if (currentTab === "audit-log" && field === "timestamp") {
            return value;
          }
          return <Chip label={value} size="small" color="primary" />;
        }
        return value;
      },
    }));

    // Add actions column (only for editable tables)
    if (!currentConfig.readOnly) {
      cols.push({
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        enableHiding: false,
        enableColumnOrdering: false,
        enableResizing: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEditClick(row.original)}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(row.original)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      });
    }
    return cols;
  }, [data, currentTab, currentConfig]);

  const formatDeletePreview = useCallback(
    (item) => {
      if (!item) return "";
      const fieldKeys = columns
        .map((col) => col.accessorKey)
        .filter((key) => key && key !== "actions");
      const filtered = fieldKeys.reduce((acc, key) => {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          acc[key] = item[key];
        }
        return acc;
      }, {});
      return JSON.stringify(sortKeysDeep(filtered), null, 2);
    },
    [columns]
  );

  const getAddButtonText = (tabKey) => {
    const buttonTextMap = {
      users: "Add User",
      statements: "Add Statement",
      "client-values": "Add Value",
      "role-definition": "Add Definition",
      "roles": "Add Role",
      "audit-log": "Add Log", // This won't show since it's read-only
    };
    return buttonTextMap[tabKey] || `Add ${tabKey}`;
  };

  // Memoize table data
  const tableData = useMemo(() => {
    const rawData = data[currentTab] || [];
    return rawData.map((row) => {
      const processedRow = { ...row };
      Object.keys(processedRow).forEach((key) => {
        if (processedRow[key] === null) {
          processedRow[key] = "";
        }
      });
      return processedRow;
    });
  }, [data, currentTab]);

  const auditUserOptions = useMemo(() => {
    const usersData = data.users || [];
    const uniqueUsers = new Set();
    usersData.forEach((user) => {
      if (user?.username) {
        uniqueUsers.add(user.username);
      }
    });
    return Array.from(uniqueUsers).sort();
  }, [data.users]);

  // Export handler
  const handleExportRows = (rows) => {
    const filteredRows = rows.map((row) => row.original);
    csvExporter.generateCsv(filteredRows);
  };

  // Generate form fields for the dialog
  const renderFormFields = () => {
    const tableData = data[currentTab] || [];

    // Helper to show snackbar messages
    const showSnackbar = (message, severity = "success") => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    };

    let fields = [];

    // Use custom columns if defined
    if (currentConfig.customColumns) {
      // Exclude auto-generated fields from form
      fields = currentConfig.customColumns
        .filter((col) => {
          // Exclude auto-generated primary key (id) in create mode
          if (
            dialogMode === "create" &&
            currentConfig.autoGenerateId &&
            col.accessorKey === currentConfig.primaryKey
          ) {
            return false;
          }
          // Exclude created timestamp in create mode (will be auto-generated)
          if (dialogMode === "create" && col.accessorKey === "created") {
            return false;
          }
          return true;
        })
        .map((col) => col.accessorKey);
    } else if (dialogMode === "create") {
      // For create mode
      if (tableData.length > 0 && tableData[0]) {
        // Use existing data structure as template
        const sampleItem = tableData[0];
        fields = Object.keys(sampleItem).filter(
          (key) =>
            key !== currentConfig.primaryKey ||
            !currentItem[currentConfig.primaryKey],
        );
      } else {
        // Default fields if no data exists yet
        fields = ["name", "description", "status", "value", "type"];
      }
    } else {
      // For edit mode
      if (!currentItem || Object.keys(currentItem).length === 0) {
        return <Typography>No data to edit</Typography>;
      }

      // Show all fields except primary/sort keys and timestamps
      fields = Object.keys(currentItem).filter(
        (key) =>
          key !== currentConfig.primaryKey &&
          key !== currentConfig.sortKey &&
          key !== "created" &&
          key !== "updated",
      );
    }

    if (fields.length === 0) {
      return (
        <Typography color="text.secondary">
          Enter field names and values manually
        </Typography>
      );
    }

    return fields.map((field) => {
      const value = currentItem[field] || "";
      const displayValue =
        typeof value === "object" ? JSON.stringify(value, null, 2) : value;

      // In edit mode, show primary/sort keys as read-only
      const isKeyField =
        field === currentConfig.primaryKey || field === currentConfig.sortKey;
      const isEditMode = dialogMode === "edit";

      // Special handling for 'active' field - use toggle buttons
      if (field === "active") {
        const isActive = value === 1 || value === "1" || value === true;
        return (
          <Box key={field} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" gutterBottom>
              {field.replace(/_/g, " ").toUpperCase()}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={isActive ? "contained" : "outlined"}
                color="success"
                size="small"
                onClick={() => handleInputChange(field, true)}
              >
                Active
              </Button>
              <Button
                variant={!isActive ? "contained" : "outlined"}
                size="small"
                onClick={() => handleInputChange(field, false)}
                sx={{
                  color: !isActive ? "white" : "text.secondary",
                  bgcolor: !isActive ? "grey.600" : "transparent",
                  borderColor: "grey.600",
                  "&:hover": {
                    bgcolor: !isActive ? "grey.700" : "grey.50",
                    borderColor: "grey.700",
                  },
                }}
              >
                Inactive
              </Button>
            </Box>
          </Box>
        );
      }

      // Special handling for 'status' field - use toggle buttons
      if (field === "status") {
        const isActive =
          value === "active" || value === "Active" || value === true;
        return (
          <Box key={field} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" gutterBottom>
              {field.replace(/_/g, " ").toUpperCase()}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={isActive ? "contained" : "outlined"}
                color="success"
                size="small"
                onClick={() => handleInputChange(field, "active")}
              >
                Active
              </Button>
              <Button
                variant={!isActive ? "contained" : "outlined"}
                size="small"
                onClick={() => handleInputChange(field, "inactive")}
                sx={{
                  color: !isActive ? "white" : "text.secondary",
                  bgcolor: !isActive ? "grey.600" : "transparent",
                  borderColor: "grey.600",
                  "&:hover": {
                    bgcolor: !isActive ? "grey.700" : "grey.50",
                    borderColor: "grey.700",
                  },
                }}
              >
                Inactive
              </Button>
            </Box>
          </Box>
        );
      }

      // Special handling for 'active' field in role-definition table - ensure boolean true/false
      if (field === "active" && currentTab === "role-definition") {
        const isActive = value === 1 || value === "1" || value === true;
        return (
          <Box key={field} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" gutterBottom>
              {field.replace(/_/g, " ").toUpperCase()}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={isActive ? "contained" : "outlined"}
                color="success"
                size="small"
                onClick={() => handleInputChange(field, true)}
              >
                Active
              </Button>
              <Button
                variant={!isActive ? "contained" : "outlined"}
                size="small"
                onClick={() => handleInputChange(field, false)}
                sx={{
                  color: !isActive ? "white" : "text.secondary",
                  bgcolor: !isActive ? "grey.600" : "transparent",
                  borderColor: "grey.600",
                  "&:hover": {
                    bgcolor: !isActive ? "grey.700" : "grey.50",
                    borderColor: "grey.700",
                  },
                }}
              >
                Inactive
              </Button>
            </Box>
          </Box>
        );
      }

      // Special handling for 'permissions' field in role-definition - use PermissionBuilder
      if (field === "permissions" && currentTab === "role-definition") {
        // Normalize value to object format
        let permissionsValue = {};
        try {
          if (typeof value === "object" && value !== null) {
            permissionsValue = value;
          } else if (typeof value === "string" && value.trim()) {
            permissionsValue = JSON.parse(value);
          }
        } catch (e) {
          // If parsing fails, start with empty object
          permissionsValue = {};
        }

        return (
          <Box key={field} sx={{ mt: 2 }}>
            <PermissionBuilder
              value={permissionsValue}
              onChange={(newPermissions) => {
                handleInputChange(field, newPermissions);
              }}
              credentials={credentials}
            />
          </Box>
        );
      }

      // Special handling for 'created' field - read-only, shown in edit mode only
      if (field === "created" && dialogMode === "edit") {
        const formattedDate = value ? new Date(value).toLocaleString() : "";
        return (
          <TextField
            key={field}
            fullWidth
            margin="normal"
            label="CREATED"
            value={formattedDate}
            disabled
            helperText="Timestamp cannot be modified"
          />
        );
      }

      // Skip created field in create mode
      if (field === "created" && dialogMode === "create") {
        return null;
      }

      // Skip id field in create mode
      if (
        field === "id" &&
        dialogMode === "create" &&
        currentConfig.autoGenerateId
      ) {
        return null;
      }

      // Show id in edit mode as read-only
      if (field === "id" && dialogMode === "edit") {
        return (
          <TextField
            key={field}
            fullWidth
            margin="normal"
            label="ID"
            value={displayValue}
            disabled
            helperText="Auto-generated ID cannot be modified"
          />
        );
      }

      // Skip ID field (uppercase) in create mode
      if (
        field === "ID" &&
        dialogMode === "create" &&
        currentConfig.autoGenerateId
      ) {
        return null;
      }

      // Show ID (uppercase) in edit mode as read-only
      if (field === "ID" && dialogMode === "edit") {
        return (
          <TextField
            key={field}
            fullWidth
            margin="normal"
            label="ID"
            value={displayValue}
            disabled
            helperText="Auto-generated ID cannot be modified"
          />
        );
      }

      // Special handling for 'role' field in role-definition table - use app roles dropdown
      if (field === "role" && currentTab === "role-definition") {
        // Get existing roles from role-definition table
        const existingRoles = (data["role-definition"] || [])
          .map(item => item.role)
          .filter(role => role != null && role !== "");
        
        // In edit mode, exclude the current role being edited (allow editing the same role)
        const rolesToDisable = dialogMode === "edit" && editingItem?.role
          ? existingRoles.filter(role => role !== editingItem.role)
          : existingRoles;
        
        const getOptionDisabled = (option) => {
          return rolesToDisable.includes(option);
        };
        
        return (
          <Autocomplete
            key={field}
            freeSolo
            options={availableAppRoles}
            value={value || ""}
            onChange={(event, newValue) => {
              handleInputChange(field, newValue);
            }}
            getOptionDisabled={getOptionDisabled}
            renderOption={(props, option) => {
              const isDisabled = getOptionDisabled(option);
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    ...props.sx,
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'default',
                    backgroundColor: isDisabled ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography sx={{ flex: 1 }}>{option}</Typography>
                    {isDisabled && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1, fontStyle: 'italic' }}
                      >
                        (already exists)
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => {
              const inputValue = params.inputProps?.value || value || "";
              const isExistingRole = rolesToDisable.includes(inputValue);
              
              return (
                <TextField
                  {...params}
                  margin="normal"
                  label="ROLE"
                  required
                  error={isExistingRole && inputValue !== ""}
                  placeholder={
                    availableAppRoles.length > 0
                      ? "Select or type an app role"
                      : "Loading app roles..."
                  }
                  helperText={
                    isExistingRole && inputValue !== ""
                      ? `Role "${inputValue}" already exists in the table`
                      : availableAppRoles.length > 0
                        ? `${availableAppRoles.length} app roles available${rolesToDisable.length > 0 ? ` (${rolesToDisable.length} already in use - greyed out)` : ''}`
                        : "No app roles found. Make sure there are active app roles in the roles table."
                  }
                />
              );
            }}
          />
        );
      }

      // Special handling for 'role' field in role-val-pairs table - use single-select dropdown
      // Note: role-val-pairs table is currently commented out in TABLES_CONFIG
      if (field === "role" && currentTab === "role-val-pairs") {
        return (
          <Autocomplete
            key={field}
            freeSolo
            options={[]}
            value={value || ""}
            onChange={(event, newValue) => {
              handleInputChange(field, newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="ROLE"
                placeholder="Select or type a role"
                helperText="Role-val-pairs table is not currently enabled"
              />
            )}
          />
        );
      }

      // Special handling for 'type' field in roles table - restrict to "data" or "app"
      if (field === "type" && currentTab === "roles") {
        return (
          <FormControl key={field} fullWidth margin="normal">
            <InputLabel>TYPE</InputLabel>
            <Select
              value={value || ""}
              onChange={(e) => handleInputChange(field, e.target.value)}
              label="TYPE"
            >
              <MenuItem value="data">Data</MenuItem>
              <MenuItem value="app">App</MenuItem>
            </Select>
          </FormControl>
        );
      }

      // Determine if field is required
      const isRequired =
        (currentTab === "role-val-pairs" && ["role", "type"].includes(field));

      // Determine placeholder
      let placeholder = "";
      if (currentTab === "role-val-pairs") {
        const placeholders = {
          role: "Select or enter a role",
          type: "Enter type (e.g., reportCategory, dataAccess)",
          filter1: "Enter filter 1 value",
          filter2: "Enter filter 2 value",
          filter3: "Enter filter 3 value",
        };
        placeholder = placeholders[field] || "";
      }

      return (
        <TextField
          key={field}
          fullWidth
          margin="normal"
          label={field.replace(/_/g, " ").toUpperCase()}
          value={displayValue}
          disabled={isEditMode && isKeyField}
          required={isRequired}
          placeholder={placeholder}
          helperText={
            isEditMode && isKeyField ? "Key fields cannot be modified" : ""
          }
          onChange={(e) => {
            let newValue = e.target.value;
            // Try to parse JSON if it looks like JSON
            if (newValue.startsWith("{") || newValue.startsWith("[")) {
              try {
                newValue = JSON.parse(newValue);
              } catch {
                // Keep as string if not valid JSON
              }
            }
            handleInputChange(field, newValue);
          }}
          multiline={
            typeof value === "object" ||
            (typeof value === "string" && value.length > 50)
          }
          rows={typeof value === "object" ? 4 : 1}
        />
      );
    });
  };

  return (
    <>
      <AntTabs
        color={props.fetchInitialData.credentials.primaryAppColor}
        value={currentTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="ant example"
      >
        {Object.entries(TABLES_CONFIG)
          .filter(
            ([key]) =>
              !SUPER_ADMIN_ONLY_TABS.includes(key) || isSuperAdmin
          )
          .map(([key, config]) => (
            <AntTab
              key={key}
              color={props.fetchInitialData.credentials.primaryAppColor}
              label={config.name}
              value={key}
            />
          ))}
      </AntTabs>

      {/* Data Grid */}
      {currentTab === "statements" ? (
        <StatementsTable credentials={credentials} />
      ) : currentTab === "system-settings" ? (
        <SystemSettings {...props} />
      ) : currentTab === "users" ? (
        <UsersTable
          usersData={data.users}
          loading={loading.users}
          error={error.users}
          credentials={credentials}
          onRefresh={() => fetchTableData("users", 0, 1000, null)}
        />
      ) : (
        <Box sx={{ flex: 1, p: 2 }}>
          <MaterialReactTable
          columns={columns}
          data={tableData}
          initialState={{
            showColumnFilters: true,
            density: "compact",
            columnPinning: { right: ["active", "actions"] },
            sorting:
              currentTab === "audit-log"
                ? [{ id: "timestamp", desc: true }]
                : [],
          }}
          muiTableContainerProps={{ sx: { maxHeight: "600px" } }}
          enableStickyHeader
          enableFullScreenToggle={false}
          enableColumnOrdering={false}
          enableColumnDragging={false}
          enableColumnPinning
          enableHiding={false}
          enableColumnFilters={true}
          enableSorting
          enableMultiSort={false}
          enablePagination={false}
          enableBottomToolbar={false}
          muiToolbarAlertBannerProps={
            error[currentTab]
              ? {
                  color: "error",
                  children: error[currentTab],
                }
              : undefined
          }
          state={{
            isLoading: loading[currentTab],
            showProgressBars: loading[currentTab],
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                p: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              {currentTab === "audit-log" && (
                <>
                  <Autocomplete
                    options={auditUserOptions}
                    value={auditFilters.user || null}
                    onChange={(_event, newValue) =>
                      handleAuditFilterChange("user", newValue || "")
                    }
                    sx={{ minWidth: 220 }}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User"
                        placeholder="Select user"
                      />
                    )}
                  />
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    value={auditFilters.startDate}
                    onChange={(event) =>
                      handleAuditFilterChange("startDate", event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    size="small"
                    value={auditFilters.endDate}
                    onChange={(event) =>
                      handleAuditFilterChange("endDate", event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleApplyAuditFilters}
                  >
                    Apply Filters
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClearAuditFilters}
                  >
                    Clear
                  </Button>
                </>
              )}
              {!currentConfig.readOnly && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  variant="contained"
                  color="primary"
                >
                  {getAddButtonText(currentTab)}
                </Button>
              )}
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                variant="outlined"
              >
                Refresh
              </Button>
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "text.secondary",
                  ml: 1,
                }}
              >
                {tableData.length} record{tableData.length !== 1 ? "s" : ""} displayed
              </Typography>
              <IconButton
                onClick={() =>
                  handleExportRows(table.getPrePaginationRowModel().rows)
                }
                title="Export to CSV"
              >
                <CloudDownloadIcon />
              </IconButton>
            </Box>
          )}
        />
        </Box>
      )}

      {/* Create/Edit Dialog (only for editable tables; users tab has its own in UsersTable) */}
      {!currentConfig.readOnly && currentTab !== "users" && (
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            {dialogMode === "create" ? "Create New Item" : "Edit Item"}
            {editingItem && !currentConfig.hideIdInTable && (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                {currentConfig.primaryKey}:{" "}
                {editingItem[currentConfig.primaryKey]}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>{renderFormFields()}</Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" color="primary">
              {dialogMode === "create" ? "Create" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this item?
            </Typography>
            {itemPendingDelete && (
              <Box
                component="pre"
                sx={{
                  backgroundColor: "#f5f5f5",
                  p: 2,
                  borderRadius: 1,
                  overflowX: "auto",
                  fontSize: "0.85rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {formatDeletePreview(itemPendingDelete)}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payload Modal */}
      <Dialog
        open={payloadModalOpen}
        onClose={handlePayloadModalClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Payload Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <pre
              style={{
                backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "4px",
                overflow: "auto",
                maxHeight: "400px",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              {payloadData ? JSON.stringify(payloadData, null, 2) : "No data"}
            </pre>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePayloadModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications - using portal to appear above dialogs */}
      {createPortal(
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ zIndex: 1500 }} // Ensure it appears above dialogs
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ fontSize: "0.875rem" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>,
        document.body,
      )}
    </>
  );
}
